import type { ClientLink } from "@orpc/client";
import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { RouterClient } from "@orpc/server";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import type { appRouter } from "../../../server/src/routers/index";

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      toast.error(`Error: ${error.message}`, {
        action: {
          label: "retry",
          onClick: () => {
            queryClient.invalidateQueries();
          },
        },
      });
    },
  }),
});

// Create RPC link with comprehensive logging
const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL || "";

// Function to get the correct RPC URL based on environment
function getRpcUrl(): string {
  if (serverUrl) {
    // Use absolute URLs for production
    const normalizedServerUrl = serverUrl.startsWith("http")
      ? serverUrl
      : `https://${serverUrl}`;
    return `${normalizedServerUrl}/rpc`;
  }

  // For development with proxy:
  if (typeof window !== "undefined") {
    // Client-side: use current origin (will be proxied to backend)
    return `${window.location.origin}/rpc`;
  }
  // SSR: connect directly to backend server
  return "http://localhost:3000/rpc";
}

// Create a function that returns a fresh link with the current URL
// change the generic to a neutral shape to avoid a strict router-type mismatch
function createLink(): RPCLink<Record<never, never>> {
  const currentUrl = getRpcUrl();

  return new RPCLink<Record<never, never>>({
    url: currentUrl,
    fetch: async (input, init) => {
      const url = typeof input === "string" ? input : input.url;
      const method = (init as RequestInit)?.method || "GET";
      const requestInit = init as RequestInit;

      // Log outgoing request details (only in development)
      if (process.env.NODE_ENV === "development") {
        logger.info("Frontend making oRPC request", {
          url,
          method,
          origin:
            typeof window !== "undefined" ? window.location.origin : "SSR",
          serverUrl: serverUrl || "proxied",
          hasHeaders: !!requestInit?.headers,
          hasBody: !!requestInit?.body,
          bodyType: requestInit?.body ? typeof requestInit.body : "none",
        });
      }

      try {
        const response = await fetch(input, init);

        // Log response details (only in development or on errors)
        if (process.env.NODE_ENV === "development" || !response.ok) {
          logger.info("Frontend received oRPC response", {
            url,
            method,
            status: response.status,
            statusText: response.statusText,
            ok: response.ok,
            corsAllowed: response.headers.get("Access-Control-Allow-Origin"),
            hasAccessControlHeaders: response.headers.has(
              "Access-Control-Allow-Origin"
            ),
          });
        }

        return response;
      } catch (error) {
        // Log fetch errors (always log errors)
        logger.error("Frontend oRPC request failed", error as Error, {
          url,
          method,
          origin:
            typeof window !== "undefined" ? window.location.origin : "SSR",
          serverUrl: serverUrl || "proxied",
        });
        throw error;
      }
    },
  });
}

// Create the link and client directly
export const link = createLink();

// Log the RPC endpoint URL for debugging
const currentRpcUrl = getRpcUrl();
logger.info("oRPC client initialized - sending requests to:", {
  endpoint: currentRpcUrl,
  originalServerUrl: process.env.NEXT_PUBLIC_SERVER_URL,
  normalizedServerUrl: serverUrl || "relative URLs (proxied)",
  currentOrigin: typeof window !== "undefined" ? window.location.origin : "SSR",
  mode: serverUrl ? "absolute URLs" : "relative URLs (Next.js proxy)",
});

// Cast link to the ClientLink generic expected by createORPCClient to resolve the TypeScript incompatibility
export const client: RouterClient<typeof appRouter> = createORPCClient(
  link as unknown as ClientLink<Record<never, never>>
);
export const orpc = createTanstackQueryUtils(client);
